import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Plus, ShoppingBag, FileIcon, Printer } from 'lucide-react';
import { toast } from 'sonner';
import Price from '../components/Price';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const categories = ['design', 'digital', 'template', 'course', 'ebook', 'art', 'music', 'software', 'other'];

// Helper to get proper image src
const getImageSrc = (img) => {
  if (!img) return null;
  if (img.startsWith('/api/')) return `${BACKEND_URL}${img}`;
  if (img.startsWith('data:')) return img;
  return `data:image/jpeg;base64,${img}`;
};

export default function MarketplacePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadProducts(); }, [filter]);

  const loadProducts = async () => {
    try {
      const params = filter !== 'all' ? `?category=${filter}` : '';
      const res = await api.get(`/products${params}`);
      setProducts(res.data);
    } catch { toast.error('Failed to load products'); }
  };

  return (
    <div className="animate-in" data-testid="marketplace-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-2xl md:text-3xl">Marketplace</h1>
        <Button
          data-testid="list-product-btn"
          onClick={() => navigate('/create-product')}
          className="rounded-sm text-xs uppercase tracking-wider h-9 px-6 bg-obsidian hover:bg-gold hover:text-obsidian transition-colors duration-300"
        >
          <Plus size={14} className="mr-1" /> Create Product
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-8 overflow-x-auto pb-2" data-testid="category-filters">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          className={`rounded-sm text-xs uppercase tracking-wider shrink-0 ${filter === 'all' ? 'bg-obsidian text-white' : ''}`}
          onClick={() => setFilter('all')}
          data-testid="filter-all"
        >All</Button>
        {categories.map(c => (
          <Button
            key={c}
            variant={filter === c ? 'default' : 'outline'}
            size="sm"
            className={`rounded-sm text-xs uppercase tracking-wider shrink-0 capitalize ${filter === c ? 'bg-obsidian text-white' : ''}`}
            onClick={() => setFilter(c)}
            data-testid={`filter-${c}`}
          >{c}</Button>
        ))}
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {products.length === 0 && (
          <p className="col-span-full text-center text-muted-foreground text-sm py-12">No products listed yet.</p>
        )}
        {products.map((p, i) => {
          const isPod = p.is_design_product || p.category === 'design';
          const imgSrc = getImageSrc(p.images?.[0]);
          
          return (
            <Link
              key={p.id}
              to={`/products/${p.id}`}
              className={`group border border-border/40 hover:border-gold/50 transition-all duration-500 animate-in ${i % 5 === 4 ? 'md:col-span-2' : ''}`}
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`product-card-${p.id}`}
            >
              <div className={`aspect-square bg-mist relative overflow-hidden ${i % 5 === 4 ? 'aspect-[2/1]' : ''}`}>
                {imgSrc ? (
                  <img src={imgSrc} alt={p.title} className="w-full h-full object-cover img-art group-hover:scale-105 transition-transform duration-700" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><ShoppingBag size={32} className="text-muted-foreground/20" /></div>
                )}
                <div className="absolute bottom-3 left-3 flex gap-1.5">
                  <Badge className="bg-white/90 text-obsidian rounded-sm text-[10px] uppercase backdrop-blur-sm">{p.category}</Badge>
                  {isPod && (
                    <Badge className="bg-purple-500/90 text-white rounded-sm text-[10px] backdrop-blur-sm">
                      <Printer size={8} className="mr-1" /> POD
                    </Badge>
                  )}
                  {!isPod && (p.file_count || 0) > 0 && (
                    <Badge className="bg-obsidian/80 text-white rounded-sm text-[10px] backdrop-blur-sm">
                      <FileIcon size={8} className="mr-1" /> {p.file_count} files
                    </Badge>
                  )}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-serif text-sm mb-1 group-hover:text-gold transition-colors">{p.title}</h3>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{p.seller_name}</span>
                  <span className="text-sm font-medium text-gold"><Price amountEur={p.price} /></span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
